import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IonInput } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AiWritingSkillsService, AiWritingSkill } from 'src/app/services/ai-writing/ai-writing-skills.service';

/**
 * The Adjust bar: one text box, a slash menu over the consultant's own saved
 * styles, and the create/edit form for those styles.
 *
 * It is its own component because it has two hosts with nothing else in common.
 * `ai-writing-field-shell` wraps a plain ion-input/ion-textarea and owns its
 * field outright; the roadmap step fields carry selection tracking,
 * press-space-to-generate and blur autosave that the shell has no place for, so
 * they host the bar directly. Keep it one component: two copies of a control
 * this stateful diverge on the first change to either.
 *
 * The host owns the model call and therefore owns `loading`, `error`, `applied`
 * and undo. The bar owns everything about styles, because saving a style is not
 * the host's business.
 */
@Component({
  selector: 'app-ai-adjust-bar',
  templateUrl: './ai-adjust-bar.component.html',
  styleUrls: ['./ai-adjust-bar.component.scss'],
})
export class AiAdjustBarComponent {
  /** A run is in flight. Disables the box and swaps the send arrow for a spinner. */
  @Input() loading = false;
  /** Drives the Undo affordance on the result tab. */
  @Input() canUndo = false;
  /** Failure text from the host's run. Beats every other tab. */
  @Input() error = '';
  /** Host's run succeeded and has not been superseded. */
  @Input() applied = false;
  /** Slug that produced the result, when a saved style did. */
  @Input() appliedSkillId = '';

  /** Instruction to run, already expanded from any `/slug`. `skillId` is the
   *  first slug used, which is all the result tab needs to name it. */
  @Output() apply = new EventEmitter<{ instruction: string; skillId: string }>();
  @Output() undo = new EventEmitter<void>();
  /** Escape pressed with nothing open — the host closes the bar. */
  @Output() dismiss = new EventEmitter<void>();
  /** A style was saved or deleted, so the host's own result tab is now stale. */
  @Output() resultSuperseded = new EventEmitter<void>();

  @ViewChild('adjustInput') adjustInputEl?: IonInput;

  instruction = '';

  // ——— Slash menu ———
  /** Row 0 is always Create style. Never filtered out, never reordered — it is
   *  the one row that can be hit without looking. */
  readonly CREATE_ROW = '__create__';
  slashOpen = false;
  slashRows: Array<{ id: string; skill?: AiWritingSkill }> = [];
  slashIndex = 0;

  // ——— Create / edit form ———
  // One form for both. `editingId` is '' when creating and the skill's current id
  // when editing, which is also what tells the form to offer Delete.
  createOpen = false;
  createName = '';
  createInstruction = '';
  editingId = '';
  /** Set when a rename would collide with a style that already exists. */
  createError = '';
  /** Distinguishes "Saved" from "Updated" on the tab. */
  savedWasEdit = false;
  savedSkillId = '';
  deletedSkillId = '';

  /** Last instruction actually sent, so Create prefills with the thing that just
   *  worked instead of making them type it a second time. */
  private lastInstruction = '';

  constructor(
    private skills: AiWritingSkillsService,
    private translate: TranslateService,
  ) {}

  /** Called by the host when it opens the bar. */
  focusInput(delayMs = 60): void {
    setTimeout(() => this.adjustInputEl?.setFocus().catch(() => {}), delayMs);
  }

  /** Called by the host when it closes the bar. */
  reset(): void {
    this.closeSlash();
    this.closeCreate();
    this.clearOwnResult();
  }

  // ——— Slash ———

  /** The `/word` being typed at the very end of the box, or null. Anchored to the
   *  end so a slash earlier in the sentence doesn't reopen the menu. */
  private slashFragment(): string | null {
    const m = (this.instruction || '').match(/(?:^|\s)\/([\w-]*)$/);
    return m ? m[1] : null;
  }

  onInstructionInput(value: string | null | undefined): void {
    this.instruction = value ?? '';
    const fragment = this.slashFragment();
    if (fragment === null) {
      this.closeSlash();
      return;
    }
    this.slashRows = [
      { id: this.CREATE_ROW },
      ...this.skills.match(fragment).map((skill: AiWritingSkill) => ({ id: skill.id, skill })),
    ];
    this.slashIndex = 0;
    this.slashOpen = true;
  }

  closeSlash(): void {
    this.slashOpen = false;
    this.slashRows = [];
    this.slashIndex = 0;
  }

  chooseSlashRow(i: number): void {
    const row = this.slashRows[i];
    if (!row) return;
    this.closeSlash();
    if (row.id === this.CREATE_ROW) {
      this.openCreate();
      return;
    }
    // Swap the fragment for the full slug and a trailing space, so the next thing
    // typed is the one-off part of the instruction rather than more of the name.
    this.instruction = this.instruction.replace(
      /(?:^|\s)\/[\w-]*$/,
      (m) => (m.startsWith(' ') ? ' ' : '') + '/' + row.id + ' ',
    );
    this.focusInput(0);
  }

  onAdjustKeydown(event: KeyboardEvent): void {
    if (this.slashOpen && this.slashRows.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.slashIndex = (this.slashIndex + 1) % this.slashRows.length;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.slashIndex = (this.slashIndex - 1 + this.slashRows.length) % this.slashRows.length;
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.chooseSlashRow(this.slashIndex);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSlash();
        return;
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitInstruction();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.dismiss.emit();
    }
  }

  /** Expands every `/slug` to its instruction and appends whatever else was
   *  typed, so "/house also mention the gap" sends both halves.
   *
   *  The boundary is load-bearing: without `(?:^|\s)` the stripper eats the
   *  slash out of `1.5 m/s`, `350 g/min`, `10 uS/cm` and `03/2024` — the
   *  everyday vocabulary of a machinery-safety instruction. Guarded by
   *  evals/ai-writing/expand.test.mjs, which reads these literals out of this
   *  file rather than copying them. */
  private expand(raw: string): string {
    const parts: string[] = [];
    // exec loop rather than matchAll: this app's TS lib target predates ES2020.
    const slug = /(?:^|\s)\/([\w-]+)/g;
    let m: RegExpExecArray | null;
    while ((m = slug.exec(raw)) !== null) {
      const skill = this.skills.get(m[1]);
      if (skill) parts.push(skill.instruction);
    }
    const rest = raw.replace(/(^|\s)\/[\w-]+/g, '$1 ').replace(/\s+/g, ' ').trim();
    if (rest) parts.push(rest);
    return parts.join(' ').trim();
  }

  submitInstruction(): void {
    const raw = (this.instruction || '').trim();
    if (!raw || this.loading) return;
    const instruction = this.expand(raw);
    if (!instruction) return;
    this.closeSlash();
    this.instruction = '';
    this.lastInstruction = instruction;
    this.clearOwnResult();
    // The first `/slug` in the box, if any — that is what the tab names.
    const m = raw.match(/(?:^|\s)\/([\w-]+)/);
    const skillId = m && this.skills.get(m[1]) ? m[1] : '';
    this.apply.emit({ instruction, skillId });
  }

  // ——— Create ———

  openCreate(): void {
    // Drop the half-typed "/" that opened the menu, then prefill from whatever is
    // left in the box, or from the instruction that just ran. Either way the text
    // is already there — a style is never retyped from scratch.
    this.instruction = this.instruction.replace(/(?:^|\s)\/[\w-]*$/, '').trim();
    this.createInstruction = this.instruction || this.lastInstruction || '';
    this.createName = '';
    this.editingId = '';
    this.createError = '';
    this.createOpen = true;
  }

  /** Opens the same form loaded with an existing style. Reached from the pencil
   *  on that style's own row, so the thing being edited is whatever the finger
   *  was already on. */
  openEdit(skill: AiWritingSkill): void {
    this.closeSlash();
    this.instruction = this.instruction.replace(/(?:^|\s)\/[\w-]*$/, '').trim();
    this.editingId = skill.id;
    this.createName = skill.id;
    this.createInstruction = skill.instruction;
    this.createError = '';
    this.createOpen = true;
  }

  closeCreate(): void {
    this.createOpen = false;
    this.createName = '';
    this.createInstruction = '';
    this.editingId = '';
    this.createError = '';
  }

  get canSaveSkill(): boolean {
    return !!this.skills.toId(this.createName) && !!(this.createInstruction || '').trim();
  }

  saveSkill(): void {
    this.createError = '';
    const saved = this.editingId
      ? this.skills.update(this.editingId, this.createName, this.createInstruction)
      : this.skills.save(this.createName, this.createInstruction);
    if (!saved) {
      // The only failure the form can't already prevent is a rename onto a name
      // that is taken — say which, rather than refusing silently.
      this.createError = this.translate.instant('A style with that name already exists.');
      return;
    }
    const wasEditing = !!this.editingId;
    this.closeCreate();
    this.clearOwnResult();
    this.resultSuperseded.emit();
    this.savedSkillId = saved.id;
    this.savedWasEdit = wasEditing;
    this.focusInput(0);
  }

  /** Deletes the style being edited. No confirm dialog: a style is a saved phrase,
   *  not a record — and it is one line to retype, whereas a modal here would be
   *  the heaviest thing in a control built to be light. */
  deleteSkill(): void {
    if (!this.editingId) return;
    const id = this.editingId;
    this.skills.remove(id);
    this.closeCreate();
    this.clearOwnResult();
    this.resultSuperseded.emit();
    this.deletedSkillId = id;
    this.focusInput(0);
  }

  private clearOwnResult(): void {
    this.savedSkillId = '';
    this.deletedSkillId = '';
    this.savedWasEdit = false;
  }
}
